import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, ArrowUpDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ContentHierarchyResponse } from '@/api/content'
import { CURRICULUM_CSV_DATA } from '@/data/curriculumCsvData'
import { compareCsvWithHierarchies, type MatchStatus, type CourseComparison } from '@/utils/csvComparison'
import { cn } from '@/lib/utils'

type FilterType = 'all' | 'matched' | 'order_mismatch' | 'csv_only' | 'system_only'

interface CsvComparisonViewProps {
  hierarchies: ContentHierarchyResponse[]
  isLoading: boolean
}

// Status labels will be translated at render time via getStatusLabel()
const STATUS_CONFIG: Record<MatchStatus, { icon: typeof CheckCircle2; color: string; labelKey: string }> = {
  matched: { icon: CheckCircle2, color: 'text-green-500', labelKey: 'learning.csv.matched' },
  order_mismatch: { icon: ArrowUpDown, color: 'text-blue-500', labelKey: 'learning.csv.orderMismatch' },
  csv_only: { icon: AlertTriangle, color: 'text-amber-500', labelKey: 'learning.csv.csvOnly' },
  system_only: { icon: XCircle, color: 'text-red-500', labelKey: 'learning.csv.systemOnly' },
}

export function CsvComparisonView({ hierarchies, isLoading }: CsvComparisonViewProps) {
  const { t } = useTranslation('learner')
  const [filter, setFilter] = useState<FilterType>('all')
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set())

  const result = useMemo(() => {
    if (!hierarchies.length) return null
    return compareCsvWithHierarchies(CURRICULUM_CSV_DATA, hierarchies)
  }, [hierarchies])

  const toggleCourse = (courseName: string) => {
    setExpandedCourses(prev => {
      const next = new Set(prev)
      if (next.has(courseName)) {
        next.delete(courseName)
      } else {
        next.add(courseName)
      }
      return next
    })
  }

  const expandAll = () => {
    if (result) {
      setExpandedCourses(new Set(result.courses.map(c => c.courseName)))
    }
  }

  const collapseAll = () => {
    setExpandedCourses(new Set())
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 border-indigo-600 animate-spin" />
          <p className="mt-4 text-gray-600">{t('learning.csv.loadingData')}</p>
        </div>
      </div>
    )
  }

  if (!result) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-gray-500">{t('learning.csv.cannotGenerateResults')}</p>
      </div>
    )
  }

  const { summary, courses } = result

  return (
    <div className="flex flex-col h-full">
      {/* Summary Bar */}
      <div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex gap-4 items-center text-sm">
            <span className="flex gap-1.5 items-center font-medium text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              {t('learning.csv.matched')} {t('learning.csv.countCases', { count: summary.matched })}
            </span>
            <span className="flex gap-1.5 items-center font-medium text-blue-700">
              <ArrowUpDown className="w-4 h-4" />
              {t('learning.csv.orderMismatch')} {t('learning.csv.countCases', { count: summary.orderMismatch })}
            </span>
            <span className="flex gap-1.5 items-center font-medium text-amber-700">
              <AlertTriangle className="w-4 h-4" />
              {t('learning.csv.csvOnly')} {t('learning.csv.countCases', { count: summary.csvOnly })}
            </span>
            <span className="flex gap-1.5 items-center font-medium text-red-700">
              <XCircle className="w-4 h-4" />
              {t('learning.csv.systemOnly')} {t('learning.csv.countCases', { count: summary.systemOnly })}
            </span>
          </div>

          <div className="flex gap-2 items-center">
            <button
              onClick={expandAll}
              className="px-2 py-1 text-xs text-gray-500 rounded transition-colors hover:text-gray-700 hover:bg-gray-200"
            >
              {t('learning.csv.expandAll')}
            </button>
            <button
              onClick={collapseAll}
              className="px-2 py-1 text-xs text-gray-500 rounded transition-colors hover:text-gray-700 hover:bg-gray-200"
            >
              {t('learning.csv.collapseAll')}
            </button>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-1 mt-3">
          {([
            { key: 'all', labelKey: 'learning.csv.all' },
            { key: 'matched', labelKey: 'learning.csv.matched' },
            { key: 'order_mismatch', labelKey: 'learning.csv.orderMismatch' },
            { key: 'csv_only', labelKey: 'learning.csv.csvOnly' },
            { key: 'system_only', labelKey: 'learning.csv.systemOnly' },
          ] as { key: FilterType; labelKey: string }[]).map(({ key, labelKey }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded-full transition-colors',
                filter === key
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Course List */}
      <div className="overflow-y-auto flex-1">
        {courses.map(course => (
          <CourseSection
            key={course.courseName}
            course={course}
            filter={filter}
            isExpanded={expandedCourses.has(course.courseName)}
            onToggle={() => toggleCourse(course.courseName)}
          />
        ))}
      </div>
    </div>
  )
}

function CourseSection({
  course,
  filter,
  isExpanded,
  onToggle,
}: {
  course: CourseComparison
  filter: FilterType
  isExpanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation('learner')

  const filteredItems = useMemo(() => {
    if (filter === 'all') return course.items
    return course.items.filter(item => item.status === filter)
  }, [course.items, filter])

  const courseStatusCounts = useMemo(() => {
    const counts = { matched: 0, order_mismatch: 0, csv_only: 0, system_only: 0 }
    for (const item of course.items) {
      counts[item.status]++
    }
    return counts
  }, [course.items])

  if (filter !== 'all' && filteredItems.length === 0) return null

  return (
    <div className="border-b border-gray-100">
      <button
        onClick={onToggle}
        className="flex gap-3 items-center px-6 py-3 w-full text-left transition-colors hover:bg-gray-50"
      >
        {isExpanded ? (
          <ChevronDown className="flex-shrink-0 w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">
            {course.courseName}
          </span>
          <span className="ml-2 text-xs text-gray-500">
            (CSV: {course.csvCount} / {t('learning.csv.system')}: {course.systemCount})
          </span>
        </div>
        <div className="flex gap-2 items-center text-xs">
          {courseStatusCounts.matched > 0 && (
            <span className="text-green-600">{courseStatusCounts.matched}</span>
          )}
          {courseStatusCounts.order_mismatch > 0 && (
            <span className="text-blue-600">{courseStatusCounts.order_mismatch}</span>
          )}
          {courseStatusCounts.csv_only > 0 && (
            <span className="text-amber-600">{courseStatusCounts.csv_only}</span>
          )}
          {courseStatusCounts.system_only > 0 && (
            <span className="text-red-600">{courseStatusCounts.system_only}</span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="pb-2">
          {filteredItems.length === 0 ? (
            <div className="px-12 py-2 text-xs text-gray-400">
              {t('learning.csv.noFilterResults')}
            </div>
          ) : (
            filteredItems.map((item, idx) => {
              const config = STATUS_CONFIG[item.status]
              const Icon = config.icon
              return (
                <div
                  key={`${item.qid || item.systemLessonId}-${idx}`}
                  className="flex gap-3 items-center px-12 py-1.5 text-sm hover:bg-gray-50"
                >
                  <Icon className={cn('w-4 h-4 flex-shrink-0', config.color)} />
                  <span className="flex-1 min-w-0 text-gray-700 truncate">
                    {item.title}
                  </span>
                  {item.qid && (
                    <span className="flex-shrink-0 text-xs text-gray-400 font-mono">
                      {item.qid}
                    </span>
                  )}
                  <span className={cn('flex-shrink-0 text-xs', config.color)}>
                    {t(config.labelKey)}
                  </span>
                  {item.status === 'order_mismatch' && item.csvIndex != null && item.systemIndex != null && (
                    <span className="flex-shrink-0 text-xs text-blue-400">
                      CSV #{item.csvIndex + 1} → {t('learning.csv.system')} #{item.systemIndex + 1}
                    </span>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
